export function formatDateParts(dateStr) {
  if (!dateStr) return { date: '—', time: '' };

  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return { date: '—', time: '' };

  return {
    date: parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }),
    time: parsed.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  };
}

export function formatShortDate(dateStr) {
  if (!dateStr) return '';

  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return '';

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
}

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const pickFiniteScore = (...values) => {
  for (const value of values) {
    const numeric = toFiniteNumber(value);
    if (numeric != null) return numeric;
  }
  return null;
};

const pickPositiveScore = (...values) => {
  for (const value of values) {
    const numeric = toFiniteNumber(value);
    if (numeric != null && numeric > 0) return numeric;
  }
  return null;
};

/**
 * Last-resort paper total, summed from the questions when the assignment carries no stored
 * `maximumScore`.
 *
 * A question inside an "attempt any X of these Y" choice group is not worth its own marks -
 * the group is worth `requiredCount * marksPerQuestion`, once, however many alternatives it
 * holds. Summing all of them would tell the student the paper is out of more than it is.
 */
const sumQuestionMaxScore = (questions, sections) => {
  if (!Array.isArray(questions) || questions.length === 0) return null;

  const choiceGroups = (Array.isArray(sections) ? sections : []).flatMap(
    (section) => section?.choiceGroups || []
  );
  const grouped = new Set(
    choiceGroups.flatMap((group) => (group?.questions || []).map(String))
  );
  // A grouped passage is paid for by its group, so its sub-questions are covered too - and so is
  // any rule among them, which is part of what the passage was offered at.
  const coveredByGroupedPassage = new Set();
  for (const question of questions) {
    if (!grouped.has(String(question?.id))) continue;
    for (const subId of question?.subQuestions || []) coveredByGroupedPassage.add(String(subId));
  }
  const paidGroups = choiceGroups.filter(
    (group) => !(group?.questions || []).some((id) => coveredByGroupedPassage.has(String(id)))
  );

  let sum = paidGroups.reduce(
    (total, group) =>
      total + (toFiniteNumber(group?.marksPerQuestion) || 0) * (toFiniteNumber(group?.requiredCount) || 0),
    0
  );
  let counted = paidGroups.length;
  for (const question of questions) {
    const type = String(question?.type || '')
      .toLowerCase()
      .replace(/[_-\s]+/g, '');
    if (type === 'readingcomprehension') continue;
    if (grouped.has(String(question?.id)) || coveredByGroupedPassage.has(String(question?.id))) continue;
    const score = toFiniteNumber(question?.score);
    if (score == null) continue;
    sum += score;
    counted += 1;
  }

  return counted > 0 ? sum : null;
};

const sumScoreMapMaxScore = (scoreMap) => {
  if (!scoreMap || typeof scoreMap !== 'object') return null;

  const entries = scoreMap instanceof Map ? [...scoreMap.values()] : Object.values(scoreMap);
  let sum = 0;
  let counted = 0;
  for (const entry of entries) {
    const maxScore = toFiniteNumber(entry?.maxScore);
    if (maxScore == null || maxScore <= 0) continue;
    sum += maxScore;
    counted += 1;
  }

  return counted > 0 ? sum : null;
};

export function getActivityScores(assignment = {}) {
  const nested =
    assignment.assignment && typeof assignment.assignment === 'object' ? assignment.assignment : {};
  const hasPairedTotal = assignment.obtainedScore != null && assignment.obtainedScore !== '';

  return {
    obtained: pickFiniteScore(
      assignment.obtainedScore,
      assignment.result?.obtainedScore,
      assignment.result?.totalScore
    ),
    total: pickPositiveScore(
      assignment.maximumScore,
      nested.maximumScore,
      hasPairedTotal ? assignment.totalScore : null,
      nested.totalScore,
      sumQuestionMaxScore(
        nested.questions || assignment.questions,
        nested.sections || assignment.sections
      ),
      sumScoreMapMaxScore(assignment.result?.scoreMap)
    )
  };
}

export function formatScore(obtained, total) {
  const earned = Number(obtained);
  const max = Number(total);
  const hasEarned = Number.isFinite(earned);
  const hasMax = Number.isFinite(max);

  if (!hasMax && (!hasEarned || earned === 0)) return null;

  const formatValue = (value) => {
    if (!Number.isFinite(value)) return '—';
    return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
  };

  const absolute = `${formatValue(hasEarned ? earned : 0)} / ${formatValue(hasMax ? max : NaN)}`;
  if (hasEarned && hasMax && max > 0) {
    const percent = Math.round((earned / max) * 100);
    return `${absolute} (${percent}%)`;
  }

  return absolute;
}
